import HTMLReactParser from 'html-react-parser';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, Flex, Heading, Separator, Text, Tooltip } from '@radix-ui/themes';

import { CheckIcon, InfoIcon } from 'shared/icons';
import { EmployerPlan } from '../for-employers.types';

type PublicPlanCardProps = {
   plan: EmployerPlan;
   currency: string;
   primaryColor: string;
};

export const normalizePlanAmount = (amount: EmployerPlan['amount']): number => {
   const value = Number(amount);
   return Number.isFinite(value) ? value : 0;
};

const renderPrice = (plan: EmployerPlan, currency: string): string => {
   const planCurrency = currency || plan.currency || '';

   return `${planCurrency} ${normalizePlanAmount(plan.amount).toLocaleString('uk-UA')}`.trim();
};

const renderPeriod = (plan: EmployerPlan, t: (key: string) => string): string => {
   const periodValue = String(plan.plan_type ?? '');

   if (plan.plan_type_value === 'Months') {
      return `${periodValue} ${
         String(plan.plan_type) === '1' ? t('membershipPlan.month') : t('membershipPlan.months')
      }`;
   }

   if (plan.plan_type_value === 'Years') {
      return `${periodValue} ${
         String(plan.plan_type) === '1' ? t('membershipPlan.year') : t('membershipPlan.years')
      }`;
   }

   return [periodValue, plan.plan_type_value].filter(Boolean).join(' ');
};

export function PublicPlanCard({ plan, currency, primaryColor }: PublicPlanCardProps) {
   const { t } = useTranslation('global');
   const freePlan = normalizePlanAmount(plan.amount) <= 0;
   const features = (plan.features ?? []).filter(
      (feature) => !!feature.outer?.trim() || !!feature.inner?.trim()
   );

   return (
      <Card className="plansPurchaseCard" size="3">
         <Flex direction="column" height="100%">
            <Box className="plansPurchaseCardTop">
               <Heading as="h4" size="4" className="plansPurchasePlanName">
                  {plan.plan_name}
               </Heading>

               <Heading
                  as="h2"
                  size="8"
                  className="plansPurchasePrice"
                  style={{ color: primaryColor }}
               >
                  {renderPrice(plan, currency)}
               </Heading>

               <Text as="p" color="gray" className="plansPurchasePeriod">
                  {renderPeriod(plan, t)}
               </Text>
            </Box>

            <Button
               className="plansPurchaseAction"
               type="button"
               size="3"
               radius="small"
               asChild
               style={{
                  borderColor: primaryColor,
                  backgroundColor: primaryColor,
               }}
            >
               <Link to="/auth/employer-login">
                  {freePlan ? t('membershipPlan.applyThisPlan') : t('membershipPlan.buyThisPlan')}
               </Link>
            </Button>

            <Separator size="4" my="5" />

            <Box asChild className="plansPurchaseFeatures">
               <ul>
                  {features.map((feature, index) => (
                     <li key={`${plan.plan_id}-${index}`}>
                        <Text as="span" color="green" className="plansPurchaseCheck">
                           <CheckIcon width={18} height={18} />
                        </Text>

                        <Text as="span" className="plansPurchaseFeatureText">
                           {feature.outer ? HTMLReactParser(feature.outer) : ''}
                        </Text>

                        {feature.inner && (
                           <Tooltip
                              content={
                                 <Text as="span" size="2">
                                    {HTMLReactParser(feature.inner)}
                                 </Text>
                              }
                           >
                              <Text as="span" color="blue" highContrast className="plansPurchaseInfo">
                                 <InfoIcon width={18} height={18} />
                              </Text>
                           </Tooltip>
                        )}
                     </li>
                  ))}
               </ul>
            </Box>
         </Flex>
      </Card>
   );
}
